import {type PDFDocumentProxy} from 'pdfjs-dist'

import {useIsNearScreen} from '#/lib/document/useIsNearScreen'
import {useDocumentChrome} from '#/components/documents/documentChrome'
import {PdfPageCanvas} from '../PdfPageCanvas.web'

/** Renders a page only once it is close to the viewport. */
export function LazyPage({
  doc,
  docKey,
  pageNumber,
  width,
  height,
  dpr,
}: {
  doc: PDFDocumentProxy
  docKey: string
  pageNumber: number
  width: number
  height: number
  dpr: number
}) {
  const chrome = useDocumentChrome()
  const {isNear, ref} = useIsNearScreen(true, '1000px 0px')
  return (
    <div
      ref={ref}
      /*
       * The page's own box, named: it is the paper's edges rather than a drawing of
       * one — a page that has not painted yet is still this size — so it is what a
       * surface drawn *over* a page is measured against (`e2e-web/
       * document-reader-column.spec.ts`).
       */
      data-testid={`documentPage-${pageNumber}`}
      style={{
        width,
        height,
        flexShrink: 0,
        // A page that has not been painted yet is a page-shaped hole. On black
        // a hole is a wash of light; on the pale canvas of the light mode it is
        // a shade darker than the canvas.
        backgroundColor: isNear ? undefined : chrome.placeholder,
        ...(chrome.sheet ? {boxShadow: chrome.sheet} : null),
      }}>
      {isNear ? (
        <PdfPageCanvas
          doc={doc}
          docKey={docKey}
          pageNumber={pageNumber}
          width={width}
          height={height}
          dpr={dpr}
        />
      ) : null}
    </div>
  )
}
